import React, { MutableRefObject, useState } from 'react';
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Modal,
  OutlinedInput,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material';
import { CardGiftcardOutlined, PersonOutlined } from '@mui/icons-material';
import * as yup from 'yup';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { shuffle } from 'lodash';
import moment from 'moment';
import { useLanguage } from '../../utils/GeneralUtils';
import PluginUtils, { PluginType } from '../PluginUtils';
import { OrderPageTextField } from '../themedComponents/OrderPageTextField';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { asOrderPageData } from '../core/types';
import { useOrderPage } from '../orderPageContext/useOrderPage';
import {
  AccountData,
  CustomPersonalDetailData,
  CustomPersonalDetailTypes,
  HolidayCalendar,
  OrderPageData,
  ReferralSource,
} from '../../api';
import { PassType } from '../../api/models/PassType';
import { isRecurringMonthly } from '../../managePassesPage/utils/passUtils';
import { RenderMultilineDetail } from './personalDetails/RenderMultilineDetail';
import { RenderBooleanDetail } from './personalDetails/RenderBooleanDetail';
import { FormStyledMenuItem, RenderDropDownDetail } from './personalDetails/RenderDropDownDetail';
import { OrderPageSelect } from '../themedComponents/OrderPageSelect';
import { OrderPageCheckbox } from '../themedComponents/OrderPageCheckbox';
import { SaveCardConsent } from '../../shared/ui/SaveCardConsent';
import { getSaveCardConsentLabel, isSaveCardConsentRequired } from '../../utils/saveCardUtils';
import { useIntentConfig } from '../intentConfig/useIntentConfig';
import { TermsConditionsCheckbox } from './personalDetails/TermsConditionsCheckbox';
import { OrderPageIntent } from '../OrderPageIntent';
import { OrderPageOutlinedTextField } from '../themedComponents/OrderPageOutlinedTextField';
import { OrderPageChoiceButton, choiceGroupSx } from '../themedComponents/OrderPageChoiceButton';
import { OrderPageCheckboxRow } from '../themedComponents/OrderPageCheckboxRow';
import { OrderPageDateField } from '../themedComponents/OrderPageDateField';
import CustomStaticDatePicker from './dateAndTimeStep/components/CustomStaticDatePicker';
import { DATE_FORMAT } from '../../consts';

type PersonalDetailsStepProps = {
  formRef: MutableRefObject<any>;
  orderPage?: OrderPageData;
};
export type PersonalDetailsValues = {
  firstname: string;
  lastname: string;
  alternative_full_name?: string;
  receipt_in_other_name?: boolean;
  email: string;
  phone: string;
  custom_personal_details: object;
  terms_conditions_checked: boolean;
  save_card_consent?: boolean;
  referral_source?: ReferralSource;
  voucher_greeting_message?: string;
  should_send_to_recipient?: boolean;
  recipient_email?: string;
  delivery_date?: string | null;
  delivery_time?: string | null;
  should_send_delayed?: boolean;
  pass_start_date?: string | null;
};
const getInitialCustomPersonalDetails = (
  customMandatoryBooleanFields: CustomPersonalDetailData[],
  customMandatoryMultilineFields: CustomPersonalDetailData[],
) => {
  if (!customMandatoryBooleanFields.length && !customMandatoryMultilineFields.length) {
    return {};
  }
  const customPersonalDetails = {};
  for (const field of customMandatoryBooleanFields) {
    // @ts-ignore
    customPersonalDetails[field.id] = false;
  }
  for (const field of customMandatoryMultilineFields) {
    // @ts-ignore
    customPersonalDetails[field.id] = '';
  }
  return customPersonalDetails;
};
export const PersonalDetailsStep = ({ formRef, orderPage: orderPageProp }: PersonalDetailsStepProps) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { orderPageState, setOrderPageState, setOrderCustomDetails } = useOrderPage();
  const { config } = useIntentConfig();
  const orderPage = orderPageProp ?? orderPageState.orderPage;
  const { orderPageIntent } = orderPageState;
  const account = orderPageState?.account as unknown as AccountData | undefined;

  const holidayCalendar = account?.holiday_calendar;
  const isGiftCardIntent =
    orderPageIntent === OrderPageIntent.GIFT_CARD || orderPageIntent === OrderPageIntent.MONETARY_GIFT_CARD;
  const Field = isGiftCardIntent ? OrderPageOutlinedTextField : OrderPageTextField;

  const language = useLanguage();
  const [weekendAndIsraeliHolidaysChecked, setWeekendAndIsraeliHolidaysChecked] = useState(false);
  const [canSpam, setCanSpam] = useState(true);
  const [isDatePickerOpen, setIsDatePickerOpen] = React.useState(false);
  const [isStartDatePickerOpen, setIsStartDatePickerOpen] = React.useState(false);
  const isBuyPassIntent = orderPageIntent === OrderPageIntent.BUY_PASS;
  const allowsReceiptName = orderPageIntent !== OrderPageIntent.REDEEM_GIFT_CARD;
  const passValidityDays = (orderPage as { validity_days?: number }).validity_days;
  const passType = (orderPage as { pass_type?: PassType }).pass_type;
  const allowsPassStartDate = isBuyPassIntent && !isRecurringMonthly(passType);
  const dateDisplayFormat = holidayCalendar === HolidayCalendar.US ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
  const accountLabel = account?.label?.[language];
  // Consent travels with saveOrder/createPass, so it is collected on this step. Skipped when no card
  // will be entered -- the backend applies the same test and does not record consent for those.
  const cardPaymentExpected =
    orderPageIntent !== OrderPageIntent.REDEEM_GIFT_CARD && orderPageState.details.price !== 0;
  const mustConsentToSaveCard =
    cardPaymentExpected && isSaveCardConsentRequired(orderPage, account, config?.paymentFormType);

  // Currently supports boolean and multiline mandatory fields
  const customMandatoryBooleanFields = (orderPage as OrderPageData).custom_personal_details?.filter(
    (x) => x.errorLabel && x.type === CustomPersonalDetailTypes.BOOLEAN,
  );
  const customMandatoryMultilineFields = (orderPage as OrderPageData).custom_personal_details?.filter(
    (x) => x.errorLabel && x.type === CustomPersonalDetailTypes.MULTILINE,
  );
  const emailValidation = yup
    .string()
    .test('is-valid-emails', t('order_page.email_valid'), (value) => {
      if (!value) return false;
      const emails = value.split(',').map((email) => email.trim());
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emails.every((email) => emailRegex.test(email));
    })
    .required(t('order_page.email_required'));
  let validationSchema = yup.object().shape({
    firstname: yup.string().required(t('order_page.firstname_required')),
    lastname: yup.string().required(t('order_page.lastname_required')),
    email: emailValidation,
    phone: yup.string().min(10, t('order_page.phone_valid')).required(t('order_page.phone_required')),
    alternative_full_name: yup.string().when('receipt_in_other_name', {
      is: true,
      then: yup.string().trim().required(t('order_page.alternative_full_name_required')),
      otherwise: yup.string().optional(),
    }),
    terms_conditions_checked: yup
      .boolean()
      .oneOf([true], t('order_page.terms_true'))
      .required(t('order_page.terms_true')),
    save_card_consent: mustConsentToSaveCard
      ? yup.boolean().oneOf([true], t('order_page.save_card_required')).required(t('order_page.save_card_required'))
      : yup.boolean().optional(),
    voucher_greeting_message: yup.string().optional(),
    should_send_to_recipient: yup.boolean().optional(),
    recipient_email: yup.string().when('should_send_to_recipient', {
      is: true,
      then: yup.string().email(t('order_page.email_valid')).required(t('order_page.email_required')),
      otherwise: yup.string().email(t('order_page.email_valid')).optional(),
    }),
  });

  if ((customMandatoryBooleanFields?.length || customMandatoryMultilineFields?.length) && !isGiftCardIntent) {
    const extraValidationSchema = {};
    for (const customField of customMandatoryBooleanFields || []) {
      // @ts-ignore
      extraValidationSchema[`${customField.id}`] = yup
        .boolean()
        .oneOf([true], customField.errorLabel?.[language])
        .required(customField.errorLabel?.[language]);
    }
    for (const customField of customMandatoryMultilineFields || []) {
      // @ts-ignore
      extraValidationSchema[`${customField.id}`] = yup.string().required(customField.errorLabel?.[language]);
    }
    validationSchema = validationSchema.shape({
      custom_personal_details: yup.object().shape(extraValidationSchema),
    });
  }
  const handleSubmit = () => {};

  const initialValues: PersonalDetailsValues = {
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    custom_personal_details: getInitialCustomPersonalDetails(
      customMandatoryBooleanFields || [],
      customMandatoryMultilineFields || [],
    ),
    terms_conditions_checked: false,
    save_card_consent: false,
    voucher_greeting_message: '',
    recipient_email: '',
    delivery_date: moment().format('YYYY-MM-DD'),
    delivery_time: moment('15:00', 'HH:mm').format('HH:mm'),
    should_send_delayed: false,
    pass_start_date: moment().format(DATE_FORMAT),
    ...(orderPageState.personalDetails as any),
    receipt_in_other_name: !!orderPageState.personalDetails?.alternative_full_name,
    should_send_to_recipient: !!orderPageState.personalDetails?.recipient_email,
  };
  // Relevant when buying voucher for weekend prices
  const handleWeekendCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWeekendAndIsraeliHolidaysChecked(event.target.checked);
    if (setOrderCustomDetails) {
      setOrderCustomDetails({ [PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS]: event.target.checked });
    }
  };
  const holidaysPlugin = PluginUtils.getPluginData(
    asOrderPageData(orderPage)?.plugins,
    PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS,
  );
  const percentString = t('order_page.weekend_checkbox', {
    percent: holidaysPlugin ? ((holidaysPlugin.multiply - 1) * 100.0).toFixed(0) : 0,
  });

  const handleCanSpam = () => {
    const newVal = !canSpam;
    setOrderPageState({ ...orderPageState, canSpam: newVal });
    setCanSpam(newVal);
  };
  const referralOptions = Object.entries(ReferralSource);
  return (
    <Formik
      enableReinitialize
      onSubmit={handleSubmit}
      initialValues={initialValues}
      innerRef={formRef}
      validationSchema={validationSchema}>
      {(formikProps) => {
        const { values, errors, touched, handleChange, handleSubmit, setFieldValue } = formikProps;
        const firstnameField = (
          <Field
            fullWidth
            className="text-field"
            id="firstname"
            name="firstname"
            label={t('order_page.firstname')}
            value={values.firstname}
            onChange={handleChange}
            error={touched.firstname && Boolean(errors.firstname)}
            helperText={touched.firstname && errors.firstname}
          />
        );
        const lastnameField = (
          <Field
            fullWidth
            className="text-field"
            id="lastname"
            name="lastname"
            label={t('order_page.lastname')}
            value={values.lastname}
            onChange={handleChange}
            error={touched.lastname && Boolean(errors.lastname)}
            helperText={touched.lastname && errors.lastname}
          />
        );
        return (
          <form onSubmit={handleSubmit}>
            {isGiftCardIntent && holidaysPlugin && (
              <Grid container flexDirection="column">
                <OrderPageTypography variant="h6">{t('order_page.extras')}</OrderPageTypography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={weekendAndIsraeliHolidaysChecked}
                      onChange={handleWeekendCheckboxChange}
                      sx={{
                        color: theme.customTheme.palette.border,
                        '&.Mui-checked': {
                          color: theme.customTheme.palette.border,
                        },
                      }}
                    />
                  }
                  label={percentString}
                />
              </Grid>
            )}
            <Grid container flexDirection="column" mt={isGiftCardIntent ? 3 : undefined} gap={2}>
              {isGiftCardIntent && (
                <OrderPageTypography variant="h6" style={{ fontWeight: 600 }}>
                  {t('order_page.gifter_personal_details')}
                </OrderPageTypography>
              )}
              {isGiftCardIntent ? (
                <Grid container columns={2} columnGap={1.5} wrap="nowrap">
                  <Grid item xs>
                    {firstnameField}
                  </Grid>
                  <Grid item xs>
                    {lastnameField}
                  </Grid>
                </Grid>
              ) : (
                <>
                  {firstnameField}
                  {lastnameField}
                </>
              )}
              <Field
                fullWidth
                className="text-field"
                id="email"
                name="email"
                label={t('order_page.email')}
                value={values.email}
                onChange={(e: any) => {
                  e.target.value = e.target.value.trim();
                  handleChange(e);
                }}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
                inputProps={isGiftCardIntent ? { dir: 'ltr' } : undefined}
              />
              <Field
                fullWidth
                className="text-field"
                id="phone"
                name="phone"
                label={t('order_page.phone')}
                type="phone"
                value={values.phone}
                onChange={handleChange}
                error={touched.phone && Boolean(errors.phone)}
                helperText={touched.phone && errors.phone}
                inputProps={isGiftCardIntent ? { dir: 'ltr' } : undefined}
              />

              {allowsReceiptName && (
                <>
                  <OrderPageCheckboxRow
                    id="receipt_in_other_name"
                    name="receipt_in_other_name"
                    checked={!!values.receipt_in_other_name}
                    onChange={() => {
                      void setFieldValue('receipt_in_other_name', !values.receipt_in_other_name);
                      void setFieldValue('alternative_full_name', '');
                    }}
                    label={t('order_page.receipt_in_other_name')}
                  />
                  {values.receipt_in_other_name && (
                    <Grid mt={isGiftCardIntent ? -1 : undefined}>
                      <Field
                        fullWidth
                        className="text-field"
                        id="alternative_full_name"
                        name="alternative_full_name"
                        label={t('order_page.alternative_full_name')}
                        value={values.alternative_full_name}
                        onChange={handleChange}
                        error={touched.alternative_full_name && Boolean(errors.alternative_full_name)}
                        helperText={touched.alternative_full_name && errors.alternative_full_name}
                      />
                    </Grid>
                  )}
                </>
              )}

              {allowsPassStartDate && (
                <>
                  <OrderPageTextField
                    fullWidth
                    className="text-field"
                    id="pass_start_date"
                    name="pass_start_date"
                    label={t('order_page.pass_start_date.picker_label')}
                    value={values.pass_start_date ? moment(values.pass_start_date).format(dateDisplayFormat) : ''}
                    onClick={() => setIsStartDatePickerOpen(true)}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                    helperText={
                      values.pass_start_date && passValidityDays != null
                        ? t('order_page.pass_start_date.preview', {
                            start: moment(values.pass_start_date).format(dateDisplayFormat),
                            end: moment(values.pass_start_date).add(passValidityDays, 'days').format(dateDisplayFormat),
                          })
                        : undefined
                    }
                  />
                  <Modal
                    open={isStartDatePickerOpen}
                    onClose={() => setIsStartDatePickerOpen(false)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <CustomStaticDatePicker
                      referenceDate={moment()}
                      displayStaticWrapperAs="desktop"
                      openTo="day"
                      disablePast
                      value={values.pass_start_date ? moment(values.pass_start_date) : moment()}
                      minDate={moment()}
                      maxDate={moment().add(6, 'months')}
                      slots={{}}
                      slotProps={{
                        day: {
                          sx: {
                            backgroundColor: 'transparent',
                            '&.Mui-disabled:not(.Mui-selected)': {
                              color: `${theme.customTheme.palette.text.disabled} !important`,
                              opacity: 0.4,
                            },
                          },
                        },
                      }}
                      onChange={(date) => {
                        void setFieldValue('pass_start_date', date?.format(DATE_FORMAT));
                        setIsStartDatePickerOpen(false);
                      }}
                      shouldDisableDate={() => false}
                      theme={theme}
                      dir={i18n.dir()}
                    />
                  </Modal>
                </>
              )}

              {!!orderPage.custom_personal_details?.length &&
                !isGiftCardIntent &&
                (orderPage as OrderPageData).custom_personal_details?.map((custom_personal_detail) => {
                  if (custom_personal_detail.type === CustomPersonalDetailTypes.MULTILINE) {
                    return (
                      <RenderMultilineDetail
                        key={custom_personal_detail.id}
                        customPersonalDetail={custom_personal_detail}
                        handleChange={handleChange}
                        values={values}
                        touched={touched}
                        errors={errors}
                      />
                    );
                  }
                  if (custom_personal_detail.type === CustomPersonalDetailTypes.BOOLEAN) {
                    return (
                      <RenderBooleanDetail
                        customPersonalDetail={custom_personal_detail}
                        setFieldValue={setFieldValue}
                        values={values}
                        touched={touched}
                        errors={errors}
                      />
                    );
                  }

                  if (custom_personal_detail.type === CustomPersonalDetailTypes.DROP_DOWN) {
                    return (
                      <RenderDropDownDetail
                        customPersonalDetail={custom_personal_detail}
                        setFieldValue={setFieldValue}
                        values={values}
                      />
                    );
                  }
                  return <div />;
                })}
            </Grid>
            {isGiftCardIntent && (
              <>
                <Divider sx={{ borderColor: '#eeeeee', my: 3 }} />
                <Grid container flexDirection="column" gap={2}>
                  <OrderPageTypography variant="h6" style={{ fontWeight: 600 }}>
                    {t('order_page.gift_section_title')}
                  </OrderPageTypography>
                  <OrderPageOutlinedTextField
                    multiline
                    minRows={3}
                    optional
                    id="voucher_greeting_message"
                    name="voucher_greeting_message"
                    label={t('order_page.voucher_card_message')}
                    placeholder={t('order_page.voucher_card_message_placeholder')}
                    value={values.voucher_greeting_message}
                    onChange={handleChange}
                    error={touched.voucher_greeting_message && Boolean(errors.voucher_greeting_message)}
                    helperText={touched.voucher_greeting_message && errors.voucher_greeting_message}
                  />
                  <Grid container flexDirection="column" gap={0.75}>
                    <div id="send-to-label">
                      <OrderPageTypography variant="body2" style={{ fontWeight: 500 }}>
                        {t('order_page.send_to')}
                      </OrderPageTypography>
                    </div>
                    <ToggleButtonGroup
                      exclusive
                      fullWidth
                      aria-labelledby="send-to-label"
                      sx={choiceGroupSx}
                      value={values.should_send_to_recipient ? 'recipient' : 'me'}
                      onChange={(e, newValue) => {
                        if (newValue === null) return;
                        const toRecipient = newValue === 'recipient';
                        void setFieldValue('should_send_to_recipient', toRecipient);
                        if (!toRecipient) {
                          void setFieldValue('recipient_email', '');
                          void setFieldValue('should_send_delayed', false);
                          void setFieldValue('delivery_date', null);
                          void setFieldValue('delivery_time', null);
                        }
                      }}>
                      <OrderPageChoiceButton value="me" icon={<PersonOutlined />}>
                        {t('order_page.send_to_me')}
                      </OrderPageChoiceButton>
                      <OrderPageChoiceButton value="recipient" icon={<CardGiftcardOutlined />}>
                        {t('order_page.send_to_recipient')}
                      </OrderPageChoiceButton>
                    </ToggleButtonGroup>
                    {!values.should_send_to_recipient && (
                      <OrderPageTypography variant="body2" style={{ color: theme.customTheme.palette.lineColor }}>
                        {values.email
                          ? t('order_page.send_to_me_hint', { email: values.email })
                          : t('order_page.send_to_me_hint_no_email')}
                      </OrderPageTypography>
                    )}
                  </Grid>
                  {values.should_send_to_recipient && (
                    <>
                      <OrderPageOutlinedTextField
                        className="text-field"
                        id="recipient_email"
                        name="recipient_email"
                        label={t('order_page.recipient_email')}
                        value={values.recipient_email}
                        onChange={handleChange}
                        error={touched.recipient_email && Boolean(errors.recipient_email)}
                        helperText={touched.recipient_email && errors.recipient_email}
                        inputProps={{ dir: 'ltr' }}
                      />
                      <Grid container flexDirection="column" gap={0.75}>
                        <div id="send-when-label">
                          <OrderPageTypography variant="body2" style={{ fontWeight: 500 }}>
                            {t('order_page.send_when')}
                          </OrderPageTypography>
                        </div>
                        <ToggleButtonGroup
                          exclusive
                          fullWidth
                          aria-labelledby="send-when-label"
                          sx={choiceGroupSx}
                          value={values.should_send_delayed ? 'scheduled' : 'immediate'}
                          onChange={(e, newValue) => {
                            if (newValue !== null) {
                              const isScheduled = newValue === 'scheduled';
                              void setFieldValue('should_send_delayed', isScheduled);
                              void setFieldValue('delivery_date', moment().format(DATE_FORMAT));
                              void setFieldValue('delivery_time', '15:00');
                              if (!isScheduled) {
                                void setFieldValue('delivery_date', null);
                                void setFieldValue('delivery_time', null);
                              }
                            }
                          }}>
                          <OrderPageChoiceButton value="immediate">
                            {t('order_page.send_immediately')}
                          </OrderPageChoiceButton>
                          <OrderPageChoiceButton value="scheduled">
                            {t('order_page.send_scheduled')}
                          </OrderPageChoiceButton>
                        </ToggleButtonGroup>
                      </Grid>
                      {values.should_send_delayed && (
                        <Grid container columns={2} columnGap={1.5} wrap="nowrap">
                          <Grid item xs>
                            <OrderPageDateField
                              id="delivery_date"
                              label={t('order_page.deliveryDate')}
                              value={
                                values.delivery_date
                                  ? moment(values.delivery_date)
                                      .locale(holidayCalendar === HolidayCalendar.US ? 'en-US' : 'en-GB')
                                      .format(holidayCalendar === HolidayCalendar.US ? 'MM/DD/YYYY' : 'DD/MM/YYYY')
                                  : ''
                              }
                              onOpen={() => setIsDatePickerOpen(true)}
                              error={touched.delivery_date && Boolean(errors.delivery_date)}
                              helperText={touched.delivery_date && errors.delivery_date}
                            />
                            <Modal
                              open={isDatePickerOpen}
                              onClose={() => setIsDatePickerOpen(false)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}>
                              <CustomStaticDatePicker
                                referenceDate={moment()}
                                displayStaticWrapperAs="desktop"
                                openTo="day"
                                disablePast
                                value={values.delivery_date ? moment(values.delivery_date) : null}
                                minDate={moment()}
                                maxDate={moment().add(6, 'months')}
                                slots={{}}
                                slotProps={{
                                  day: {
                                    sx: ({ disabled }: { disabled: boolean }) => ({
                                      backgroundColor: 'transparent',
                                      '&.Mui-disabled': {
                                        color: `${theme.customTheme.palette.text.disabled} !important`,
                                      },
                                      color: disabled
                                        ? `${theme.customTheme.palette.error} !important`
                                        : theme.customTheme.palette.text.primary,
                                    }),
                                  },
                                }}
                                onChange={(date) => {
                                  void setFieldValue('delivery_date', date?.format(DATE_FORMAT));
                                  setIsDatePickerOpen(false);
                                }}
                                shouldDisableDate={() => false}
                                theme={theme}
                                dir="ltr"
                              />
                            </Modal>
                          </Grid>
                          <Grid item xs>
                            <Grid container flexDirection="column" gap={0.75}>
                              <div id="delivery-time-label">
                                <OrderPageTypography variant="body2" style={{ fontWeight: 500 }}>
                                  {t('order_page.deliveryTime')}
                                </OrderPageTypography>
                              </div>
                              <FormControl fullWidth>
                                <OrderPageSelect
                                  labelId="delivery-time-label"
                                  id="delivery_time"
                                  name="delivery_time"
                                  value={values.delivery_time}
                                  input={
                                    <OutlinedInput
                                      sx={{
                                        height: 48,
                                        borderRadius: '8px',
                                        backgroundColor: theme.customTheme.palette.background,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderColor: theme.customTheme.palette.border,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                          borderColor: theme.customTheme.palette.primary,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                          borderColor: theme.customTheme.palette.primary,
                                          borderWidth: 1,
                                        },
                                      }}
                                    />
                                  }
                                  onChange={(e) => {
                                    void setFieldValue('delivery_time', e.target.value);
                                  }}>
                                  {['09', '12', '15', '18', '21', '00'].map((hour) => {
                                    const timeDisplay =
                                      holidayCalendar === HolidayCalendar.US
                                        ? moment(hour, 'HH').format('hh:mm A')
                                        : `${hour}:00`;
                                    return (
                                      <FormStyledMenuItem
                                        key={`hour-${hour}`}
                                        value={`${hour}:00`}
                                        textColor={theme.customTheme.palette.text.primary}
                                        backgroundColor={theme.customTheme.palette.background}>
                                        {timeDisplay}
                                      </FormStyledMenuItem>
                                    );
                                  })}
                                </OrderPageSelect>
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </>
            )}
            <Grid container flexDirection="column" item>
              <FormControl fullWidth variant="standard">
                <InputLabel id={'select-source-input'}>
                  <OrderPageTypography>{t('order_page.refer')}</OrderPageTypography>
                </InputLabel>
                <OrderPageSelect
                  labelId={'select-source-input'}
                  id={'referral_source'}
                  name={'referral_source'}
                  value={values.referral_source}
                  onChange={handleChange}
                  renderValue={(v) =>
                    v ? <OrderPageTypography>{t(`order_page.refferOptions.${v}`)}</OrderPageTypography> : ''
                  }>
                  {shuffle(referralOptions).map(([key, value]) => (
                    <FormStyledMenuItem
                      key={`MenuItem-${key}`}
                      value={value}
                      textColor={theme.customTheme.palette.text.primary}
                      backgroundColor={theme.customTheme.palette.background}>
                      {t(`order_page.refferOptions.${value}`)}
                    </FormStyledMenuItem>
                  ))}
                </OrderPageSelect>
              </FormControl>
            </Grid>
            <Grid container flexDirection="column" mt={2}>
              <FormControlLabel
                control={<OrderPageCheckbox checked={canSpam} onChange={handleCanSpam} />}
                label={t('order_page.can_spam', { accountLabel })}
              />

              {mustConsentToSaveCard && (
                <SaveCardConsent
                  checked={!!values.save_card_consent}
                  onChange={(checked) => setFieldValue('save_card_consent', checked)}
                  label={getSaveCardConsentLabel(orderPage, language)}
                  isRecurring={isRecurringMonthly(passType)}
                  showError={!!touched.save_card_consent && !!errors.save_card_consent}
                  CheckboxComponent={OrderPageCheckbox}
                  errorColor={theme.customTheme.palette.error}
                />
              )}

              <TermsConditionsCheckbox
                account={account}
                checked={values.terms_conditions_checked}
                onChange={handleChange}
                touched={touched.terms_conditions_checked}
                error={errors.terms_conditions_checked}
              />
            </Grid>
          </form>
        );
      }}
    </Formik>
  );
};
